import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../services/api';
import { User } from '../models/types';
import { LoginCredentials, RegisterData } from '../services/api/domains/auth';
import { ROUTES } from '../config/routes';
import { jwtDecode } from 'jwt-decode';
import { publicNavigation, NavigationItem } from '../navigation/config';

type UserRole = 'student' | 'teacher' | 'organization';

interface AuthContextData {
    user: User | null;
    loading: boolean;
    login: (credentials: LoginCredentials) => Promise<void>;
    logout: () => Promise<void>;
    register: (data: RegisterData) => Promise<void>;
    userRole: UserRole | null;
    isAuthenticated: boolean;
}

interface JWTPayload {
    role: string;
    sub: string;
    district_id: number;
    school_id: number;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const isValidRole = (role: string | null): role is UserRole => {
    return role === 'student' || role === 'teacher' || role === 'organization';
};

const getRoleRoute = (role: string): string => {
    switch (role) {
        case 'student':
            return ROUTES.STUDENT.DASHBOARD;
        case 'teacher':
            return ROUTES.TEACHER.DASHBOARD;
        case 'organization':
            return ROUTES.ORGANIZATION.DASHBOARD;
        default:
            console.error('Unhandled role type:', role);
            return ROUTES.LOGIN;
    }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [userRole, setUserRole] = useState<UserRole | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Initialize auth state from localStorage
    useEffect(() => {
        const initializeAuth = () => {
            const token = localStorage.getItem('auth_token');
            if (!token) {
                setLoading(false);
                return;
            }

            try {
                const decoded = jwtDecode<JWTPayload>(token);
                if (!decoded.role || !isValidRole(decoded.role)) {
                    throw new Error('Invalid role in token');
                }

                api.setToken(token);
                setUserRole(decoded.role);
                setIsAuthenticated(true);

                // Create minimal user object from token
                const user: User = {
                    id: decoded.sub,
                    role: decoded.role,
                    district_id: decoded.district_id,
                    school_id: decoded.school_id,
                    email: '' // We'll get this from the profile if needed
                };
                setUser(user);
            } catch (error) {
                console.error('Invalid token:', error);
                handleLogout();
            } finally {
                setLoading(false);
            }
        };

        initializeAuth();
    }, []);

    // Handle route protection
    useEffect(() => {
        if (loading) return;

        // Get all public paths from the navigation config
        const publicPaths = [
            ROUTES.LOGIN,
            ROUTES.REGISTER,
            ROUTES.HOME,
            ...publicNavigation.flatMap((item: NavigationItem) => [
                item.path,
                ...(item.children?.map((child: NavigationItem) => child.path) || [])
            ])
        ];

        const isPublicPath = publicPaths.includes(location.pathname);
        const isProtectedPath = !isPublicPath;

        // Only redirect to login if accessing a protected path while unauthenticated
        if (!isAuthenticated && isProtectedPath) {
            navigate(ROUTES.LOGIN, { state: { from: location.pathname }, replace: true });
        } else if (isAuthenticated && location.pathname === ROUTES.LOGIN && userRole) {
            // Redirect authenticated users away from login
            navigate(getRoleRoute(userRole), { replace: true });
        }
    }, [isAuthenticated, loading, location.pathname, navigate, userRole]);

    const handleLogout = useCallback(async () => {
        try {
            // Don't await the logout call since it's failing with 404
            api.auth.logout().catch(console.warn);
        } finally {
            setUser(null);
            setUserRole(null);
            setIsAuthenticated(false);
            api.setToken(null);
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_role');
            navigate(ROUTES.LOGIN, { replace: true });
        }
    }, [navigate]);

    const login = useCallback(async (credentials: LoginCredentials) => {
        try {
            setLoading(true);
            const response = await api.auth.login(credentials);
            
            console.log('Login response:', response);
            
            if (!response?.data) {
                throw new Error('No response received from server');
            }

            const { token, user } = response.data;
            console.log('Token and user:', { token: !!token, user });

            const decoded = jwtDecode<JWTPayload>(token);
            console.log('Decoded token:', decoded);

            if (!decoded.role || !isValidRole(decoded.role)) {
                console.error('Invalid role in token:', decoded);
                throw new Error('Invalid user role received from server');
            }
            
            // Use the user data from the response
            const userData: User = {
                ...user,
                id: decoded.sub,
                role: decoded.role,
                district_id: decoded.district_id,
                school_id: decoded.school_id,
            };
            
            api.setToken(token);
            localStorage.setItem('auth_token', token);
            localStorage.setItem('user_role', decoded.role);
            
            setUser(userData);
            setUserRole(decoded.role);
            setIsAuthenticated(true);

            // Navigate to the appropriate dashboard
            navigate(getRoleRoute(decoded.role), { replace: true });
        } catch (error) {
            console.error('Login error details:', {
                error,
                isError: error instanceof Error,
                message: error instanceof Error ? error.message : 'Unknown error',
                stack: error instanceof Error ? error.stack : undefined
            });
            
            // Clear auth state
            setUser(null);
            setUserRole(null);
            setIsAuthenticated(false);
            api.setToken(null);
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_role');
            
            // Rethrow with more descriptive error
            if (error instanceof Error) {
                throw new Error(`Login failed: ${error.message}`);
            }
            throw new Error('Login failed: An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    const register = useCallback(async (data: RegisterData) => {
        try {
            setLoading(true);
            const response = await api.auth.register(data);
            console.log('Register response:', response);
            
            if (!response?.data) {
                throw new Error('No response received from server');
            }

            const { token } = response.data;
            console.log('Token:', !!token);
            
            const decoded = jwtDecode<JWTPayload>(token);
            console.log('Decoded token:', decoded);

            if (!decoded.role || !isValidRole(decoded.role)) {
                throw new Error('Invalid user role received from server');
            }

            const user: User = {
                id: decoded.sub,
                role: decoded.role,
                district_id: decoded.district_id,
                school_id: decoded.school_id,
                email: data.email
            };

            api.setToken(token);
            localStorage.setItem('auth_token', token);
            localStorage.setItem('user_role', decoded.role);
            
            setUser(user);
            setUserRole(decoded.role);
            setIsAuthenticated(true);

            // Navigate to the appropriate dashboard
            navigate(getRoleRoute(decoded.role), { replace: true });
        } catch (error) {
            console.error('Register error:', error);
            handleLogout();
            throw error;
        } finally {
            setLoading(false);
        }
    }, [handleLogout, navigate]);

    return (
        <AuthContext.Provider value={{ 
            user, 
            loading, 
            login, 
            logout: handleLogout, 
            register, 
            userRole,
            isAuthenticated 
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);